import { Component, computed, inject } from '@angular/core';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	ExportDataOptions,
	ExportFilter,
	FilterExpression,
} from '../../services/data.service';
import { ExportColumnDef } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { CardListConfig } from '../../shared/dashboard/card-list/card-list-config';
import { Attributes } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { Observable } from 'rxjs';
import { concat, flatMap, isNil, isUndefined, last, memoize } from 'lodash-es';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { Router } from '@angular/router';
import { Level, Path } from '../../services/data-tree';
import { aggArrayString, maxOver, MultiAggregator, noAgg0, noAggString } from '../../services/aggregation';
import {
	DRIE_SCHOOLJAREN_EXCL_2019,
	DRIE_SCHOOLJAREN_EXCL_2020,
	getOnderwijsresultatenSchooljaarRangeString,
	getOnderwijsresultatenSchooljarenRange,
	getPeiljaar,
	getSchooljaarTovHuidig,
	getSchooljarenRange,
	isPeiljaarMetExclusief,
	roundToDecimals,
	SchooljaarHistorieOption,
} from '@cumlaude/shared-utils';
import { CardOrCellComponent, OnderwijsresultatenInputRow } from './card-or-cell/card-or-cell.component';
import { AttrPath, Table } from '@cumlaude/metadata';
import { LinechartProps, PointInfo } from '../../shared/dashboard/linechart-table/linechart/linechart.component';
import { formatNumber, formatPercent } from '@angular/common';
import { createYAxis } from '../../services/axis';
import { DashboardVariant } from '../../services/weergave-opties';
import { indicatorOverOpties, SchooljaarIndicatorOverComponent } from '../../schooljaar-indicator-over/schooljaar-indicator-over.component';
import { PageStateService, PsName } from '../../services/page-state.service';
import { CardOrRowCellComponent } from './card-or-row-cell/card-or-row-cell.component';
import { CardOrSingleCellComponent } from './card-or-single-cell/card-or-single-cell.component';
import { CardListComponent } from '../../shared/dashboard/card-list/card-list.component';
import { TooltipDirective, TooltipElement } from '@cumlaude/shared-components-overlays';
import { CheckboxComponent } from '@cumlaude/shared-components-inputs';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { ExportOptions } from '../../services/export.service';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface OnderwijsresultatenI extends Attributes {
	or_m_nm_cl_schooljaren: string;
	or_m_nm_inspectie_schooljaren: string;
	or_m_nm_vestigingen: string;
	or_m_nr_norm_gecorrigeerd: number;
	or_m_nr_norm_landelijk: number;
	or_m_nr_score: number;
	or_m_nr_verschil: number;
	or_m_q_leerlingen: number;
	or_fun_nr_peiljaar: number;
	or_m_is_prognose: number;
}

export interface OnderwijsresultatenA extends Attributes {
	or_m_nm_cl_schooljaren: string;
	or_m_nm_inspectie_schooljaren: string;
	or_m_nm_vestigingen: string;
	or_m_nr_norm_gecorrigeerd: number;
	or_m_nr_norm_landelijk: number;
	or_m_nr_score: number;
	or_m_nr_verschil: number;
	or_m_q_leerlingen: number;
	or_fun_nr_peiljaar: number;
	minScoreNorm: number;
	maxScoreNorm: number;
	/**
	 * De bron van de data in een bitset (eerste bit is wel/niet cumlaude, tweede bit is wel/niet inspectie)
	 */
	bron: number;
	or_m_is_prognose: number;
}

interface NiveauOnderNorm {
	niveau: string;
	bbs: boolean;
	exc: boolean;
}

/**
 * CL-9331: beide onderbouwindicatoren onder de norm geeft alleen de generieke pill, nooit gecombineerd met een niveau-pill.
 */
export function computeOnvoldoendeLabels(op: boolean, obs: boolean, niveaus: NiveauOnderNorm[]): string[] {
	const onderbouwCount = Number(op) + Number(obs);

	if (onderbouwCount === 2) return ['Onvoldoende'];

	return niveaus
		.filter(({ bbs, exc }) => {
			const bovenbouwCount = Number(bbs) + Number(exc);
			return onderbouwCount + bovenbouwCount >= 2;
		})
		.map(({ niveau }) => niveau);
}

@Component({
	selector: 'app-onderwijsresultaten',
	templateUrl: './onderwijsresultaten.component.html',
	styleUrls: ['./onderwijsresultaten.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		TooltipDirective,
		SchooljaarIndicatorOverComponent,
		CardListComponent,
		CardOrCellComponent,
		CardOrSingleCellComponent,
		CardOrRowCellComponent,
		FilterBarComponent,
		CheckboxComponent,
		IconComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class OnderwijsresultatenComponent extends CardListConfig<OnderwijsresultatenI, Attributes, OnderwijsresultatenA> {
	protected readonly dataService = inject(DataService);
	protected readonly router = inject(Router);
	protected readonly pageStateService = inject(PageStateService);

	actueelFilters: FilterName[] = [
		'x_onderwijsresultaten_or_schooljaar', //
		'or_co_brinvest',
	];

	historieFilters: FilterName[] = [
		'x_onderwijsresultaten_or_schooljaar', // is onzichtbaar en filtert niet, maar is alleen om het schooljaar van de actueel-variant vast te houden
		'x_onderwijsresultaten_or_schooljaar_historie',
		'x_onderwijsresultaten_or_multiselect_schooljaar',
		'or_co_brinvest',
	];

	groups: AttrPath[] = [['or_co_brinvest']];

	subGroups: AttrPath[] = [['or_co_indicator'], ['or_nm_soort_onderwijs'], ['or_fun_nr_peiljaar']];

	aantalJaren!: number;

	useInspectieData = this.qp.signal('inspectie-data');
	toonTrend = this.qp.signal('toon-trend');
	variant = this.qp.signal('variant');

	readonly inspectiedataTooltip = 'Toon inspectiedata waar beschikbaar. Per indicator kun je inzien of er inspectie- of brondata wordt gebruikt.';
	readonly trendindicatiesTooltip = 'Toon trendindicaties die de ontwikkeling van scores ten opzichte van het voorgaande jaar weergeeft.';

	schooljaar?: string = undefined;

	peiljaar?: number = undefined;

	peiljaren: number[] = [];

	niveau?: string[] = undefined;

	bron: string = 'Onbekend';

	readonly permanentFilters = computed(() => {
		const permanentFilters = [new BasicFilterExpression(['or_co_indicator'], 'cv', '<>')];
		return this.useInspectieData() ? permanentFilters : [new BasicFilterExpression(['or_nm_bron'], 'cl', '='), ...permanentFilters];
	});

	readonly filterPanelPermanentFilters = computed(() => [new BasicFilterExpression(['or_nr_peiljaar'], null, '='), ...this.permanentFilters()]);

	heeftOnvoldoendePillMeasure = (row: Path<OnderwijsresultatenA, unknown>) =>
		this.getOnvoldoendeLabels(row as Path<OnderwijsresultatenA, number[]>, this.peiljaar!).length > 0 ? 1 : 0;

	constructor() {
		super();

		this.subscriptions.push(
			this.filterService.observe('x_onderwijsresultaten_or_schooljaar').subscribe(([schooljaar, aantalJaren]) => {
				if (this.variant() === DashboardVariant.HISTORIE) return;

				this.schooljaar = schooljaar;
				this.aantalJaren = aantalJaren;
				this.peiljaar = Number(schooljaar.split('/')[1]) + 1;
				this.peiljaren = [this.peiljaar];
			}),
			this.filterService.observe('x_onderwijsresultaten_or_schooljaar_historie').subscribe(([{ option, inclHuidig }, aantalJaren]) => {
				if (this.variant() === DashboardVariant.ACTUEEL) return;

				if (option === SchooljaarHistorieOption.AANGEPAST) return;

				this.aantalJaren = aantalJaren;

				let aantal = 0;
				switch (option) {
					case SchooljaarHistorieOption.AFGELOPEN_3:
						aantal = 3;
						break;
					case SchooljaarHistorieOption.AFGELOPEN_5:
						aantal = 5;
						break;
					case SchooljaarHistorieOption.AFGELOPEN_10:
						aantal = 10;
						break;
				}

				const laatsteJaar = getSchooljaarTovHuidig(inclHuidig ? 0 : -1);
				this.peiljaren = getSchooljarenRange(laatsteJaar, aantal).map((schooljaar) => Number(schooljaar.split('/')[1]) + 1);
				this.peiljaar = Number(laatsteJaar.split('/')[1]) + 1;
			}),
			this.filterService.observe('x_onderwijsresultaten_or_multiselect_schooljaar').subscribe((value) => {
				if (isUndefined(value)) return;

				if (this.variant() === DashboardVariant.ACTUEEL) return;

				const [schooljaren, aantalJaren]: [string[], number] = value;

				this.aantalJaren = aantalJaren;
				this.peiljaren = schooljaren.map((schooljaar: string) => Number(schooljaar.split('/')[1]) + 1).sort((a, b) => a - b);
				this.peiljaar = Math.max(...this.peiljaren);
			}),
			this.filterService.observe('or_nm_soort_onderwijs').subscribe((niveau) => (this.niveau = niveau))
		);
	}

	factTable = Table.fac_or_onderwijsresultaten;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getOnderwijsresultatenData({
			...options,
			peiljaarThreshold: this.variant() === DashboardVariant.ACTUEEL ? this.peiljaar : undefined,
			schooljaarThreshold: this.aantalJaren >= 3 ? 2 : undefined,
		});
	}

	protected override singleAggregators = {
		or_m_nr_norm_gecorrigeerd: noAgg0('or_m_nr_norm_gecorrigeerd'),
		or_m_nr_norm_landelijk: noAgg0('or_m_nr_norm_landelijk'),
		or_m_nr_score: noAgg0('or_m_nr_score'),
		or_m_nr_verschil: noAgg0('or_m_nr_verschil'),
		or_m_q_leerlingen: noAgg0('or_m_q_leerlingen'),
		or_fun_nr_peiljaar: noAgg0('or_fun_nr_peiljaar'),
		or_m_nm_cl_schooljaren: noAggString('or_m_nm_cl_schooljaren'),
		or_m_nm_inspectie_schooljaren: noAggString('or_m_nm_inspectie_schooljaren'),
		or_m_nm_vestigingen: aggArrayString('or_m_nm_vestigingen', (attrs: OnderwijsresultatenI) =>
			this.peiljaren.includes(Number(attrs.or_fun_nr_peiljaar))
		),
		bron: {
			init: (v: OnderwijsresultatenI) => {
				const cumlaude = isNil(v.or_m_nm_cl_schooljaren) ? 0 : 1;
				const inspectie = isNil(v.or_m_nm_inspectie_schooljaren) ? 0 : 1;
				return cumlaude | (inspectie << 1);
			},
			combine: (as: number[]) => as.reduce((a, b) => a | b, 0),
		},
		or_is_prognose: maxOver('or_is_prognose'),
	};

	protected override multiAggregators: MultiAggregator<keyof OnderwijsresultatenA, OnderwijsresultatenI, OnderwijsresultatenA, number | string>[] =
		[
			{
				attribute: 'minScoreNorm',
				init: ({ or_m_nr_score, or_m_nr_norm_gecorrigeerd, or_m_nr_norm_landelijk }) =>
					Math.min(or_m_nr_score, or_m_nr_norm_gecorrigeerd, or_m_nr_norm_landelijk),
				combine: (as) => Math.min(...as.map((a) => a.minScoreNorm)),
			},
			{
				attribute: 'maxScoreNorm',
				init: ({ or_m_nr_score, or_m_nr_norm_gecorrigeerd, or_m_nr_norm_landelijk }) =>
					Math.max(or_m_nr_score, or_m_nr_norm_gecorrigeerd, or_m_nr_norm_landelijk),
				combine: (as) => Math.max(...as.map((a) => a.maxScoreNorm)),
			},
		];

	getSchooljarenCaption() {
		return this.schooljaar ? getOnderwijsresultatenSchooljaarRangeString(this.schooljaar, this.aantalJaren, 'bbs') : '';
	}

	getSchooljarenTooltip() {
		if (!isPeiljaarMetExclusief(this.schooljaar) || this.aantalJaren < DRIE_SCHOOLJAREN_EXCL_2020) {
			const onderwijspositieSchooljarenInspectie = getOnderwijsresultatenSchooljaarRangeString(this.schooljaar!, this.aantalJaren, 'op', true);
			return `Let op: bij de indicator Onderwijspositie toont onderwijsinspectie deze periode als ${onderwijspositieSchooljarenInspectie}`;
		}

		const tooltipElements: TooltipElement[] = [];
		for (const indicator of ['op', 'obs', 'bbs', 'exc']) {
			const indicatorNaam = this.getIndicatorNaam(indicator);

			let indicatorTekst = getOnderwijsresultatenSchooljaarRangeString(this.schooljaar!, this.aantalJaren, indicator);
			if (indicator === 'op') {
				const inspectieSchooljaren = getOnderwijsresultatenSchooljaarRangeString(this.schooljaar!, this.aantalJaren, 'op', true);
				indicatorTekst += ` (Inspectie toont dit als ${inspectieSchooljaren})`;
			} else if (indicator === 'exc' && [2022, 2023, 2024].includes(getPeiljaar(this.schooljaar!))) {
				indicatorTekst = 'Onvoldoende data';
			}

			tooltipElements.push({ label: indicatorNaam, value: indicatorTekst });
		}

		return tooltipElements;
	}

	onClick(path: Path<OnderwijsresultatenA, number[]>, indicator: string) {
		const dashboard = this.getDashboard(indicator);
		if (!dashboard) return;

		const queryParameters = this.getQueryParameters(path, indicator);
		if (!queryParameters) return;

		this.urlService.redirect([dashboard], queryParameters);
	}

	private isOnderNorm(vestiging: Level<OnderwijsresultatenA, number[]>, indicator: string, niveau: string | null, peiljaar: number): boolean {
		const niveauLevel = vestiging.c.find((level) => level.k === indicator)?.c.find((level) => level.k === niveau);
		const peiljaarLevel = niveauLevel?.c.find((level) => Number(level.k) === peiljaar);
		return (peiljaarLevel?.a.or_m_nr_verschil ?? 0) < 0;
	}

	getOnvoldoendeLabels(row: Path<OnderwijsresultatenA, number[]>, peiljaar: number): string[] {
		const vestiging = last(row)!;
		const niveaus = new Set<string>();
		for (const indicator of ['bbs', 'exc']) {
			for (const niveauLevel of vestiging.c.find((level) => level.k === indicator)?.c ?? []) {
				if (niveauLevel.k !== null) niveaus.add(niveauLevel.k);
			}
		}

		return computeOnvoldoendeLabels(
			this.isOnderNorm(vestiging, 'op', null, peiljaar),
			this.isOnderNorm(vestiging, 'obs', null, peiljaar),
			[...niveaus].map((niveau) => ({
				niveau,
				bbs: this.isOnderNorm(vestiging, 'bbs', niveau, peiljaar),
				exc: this.isOnderNorm(vestiging, 'exc', niveau, peiljaar),
			}))
		);
	}

	override onContextCreated(context: DashboardContext<OnderwijsresultatenI, OnderwijsresultatenA, OnderwijsresultatenComponent>) {
		const dataRoot = context.dataRoot;
		if (isUndefined(dataRoot)) {
			this.bron = 'Onbekend';
			return;
		}

		this.bron = ['Bronsysteem', 'Inspectie', 'Bronsysteem & Inspectie'][dataRoot.a.bron - 1];
		this.pageStateService.dispatch(PsName.prognose, String(Boolean(dataRoot.a.or_m_is_prognose)));
	}

	private getQueryParameters(path: Path<OnderwijsresultatenA, number[]>, indicator: string) {
		const params = {};
		const exc = ['exc'].includes(indicator);

		let schooljaar: string | undefined;
		if (this.variant() === DashboardVariant.HISTORIE) {
			const peiljaarPos = this.groups.length + this.subGroups.findIndex((value) => value.join('.') === 'or_fun_nr_peiljaar') + 1;
			const peiljaar = path[peiljaarPos]?.k;
			if (peiljaar) schooljaar = `${Number(peiljaar) - 2}/${Number(peiljaar) - 1}`;
		} else if (this.variant() === DashboardVariant.ACTUEEL) schooljaar = this.schooljaar;

		if (!schooljaar) return undefined;

		const filterName = exc ? 'x_onderwijsresultaten_cf_schooljaar' : 'x_onderwijsresultaten_ds_schooljaar';
		Object.assign(params, this.filterService.encodeFilterValue(filterName, schooljaar));

		const vestigingPos = this.groups.findIndex((value) => value.join('.') === 'or_co_brinvest') + 1;
		const vestiging = path[vestigingPos].k;
		if (vestiging) {
			const vestigingBrinFilter = exc ? 'cf_fk_br_vest.br_co_brin' : 'ds_fk_br_vest_van.br_co_brin';

			const valueParts = vestiging.split('(');
			Object.assign(params, this.filterService.encodeFilterValue(vestigingBrinFilter, valueParts[0].trim()));
		}

		const niveau = this.niveau;
		if (niveau) {
			const filterName = exc ? 'cf_fk_ilt.ilt_nm_niveau' : 'ds_fk_ilt_van.ilt_nm_niveau';
			Object.assign(params, this.filterService.encodeFilterValue(filterName, niveau));
		}

		return params;
	}

	private getDashboard(indicator: string): string | undefined {
		switch (indicator) {
			case 'op':
				return 'onderwijsresultaten/onderwijspositie';
			case 'obs':
				return 'onderwijsresultaten/onderbouwsnelheid';
			case 'bbs':
				return 'onderwijsresultaten/bovenbouwsucces';
			case 'exc':
				return 'onderwijsresultaten/examencijfers';
		}
		return undefined;
	}

	getCellRows = memoize(this._getCellRows, (variant, row, ind, pj, pjn, a, f) =>
		JSON.stringify([variant, row.map((level) => level.k), ind, pj, pjn, a, f])
	);

	private _getCellRows(
		variant: DashboardVariant,
		row: Path<OnderwijsresultatenA, number[]>,
		indicator: string,
		peiljaar: number,
		peiljaren: number[],
		aantalJaren: number,
		_filter: FilterExpression
	): OnderwijsresultatenInputRow[] {
		if (variant == DashboardVariant.ACTUEEL) return this.getActueelCellRows(row, indicator, peiljaar, aantalJaren);
		else return this.getHistorieCellRows(row, indicator, peiljaar, peiljaren);
	}

	private getActueelCellRows(
		row: Path<OnderwijsresultatenA, number[]>,
		indicator: string,
		peiljaar: number,
		aantalJaren: number
	): OnderwijsresultatenInputRow[] {
		const indicatorRows = row[1].c.find((level) => level.k == indicator);
		if (isUndefined(indicatorRows)) return [];

		const schooljaar1 = peiljaar;
		const schooljaar2 = peiljaar - 1;
		const schooljaar3 = peiljaar - 2;
		// Voor onderwijspositie slaan we 2019/2020 over omdat de inspectie dat 2020/2021 noemt (en dat jaar dus overslaat)
		const gecorrigeerdAantalJaren = indicator === 'op' && aantalJaren === DRIE_SCHOOLJAREN_EXCL_2020 ? DRIE_SCHOOLJAREN_EXCL_2019 : aantalJaren;

		return indicatorRows.c
			.filter((cell) => !isUndefined(cell.c.find((level) => Number(level.k) == schooljaar1)))
			.map((cell) => {
				const jaar1 = cell.c.find((level) => Number(level.k) == schooljaar1)!;
				const jaar2 = cell.c.find((level) => Number(level.k) == schooljaar2);
				const jaar3 = cell.c.find((level) => Number(level.k) == schooljaar3);
				const bron_schooljaren = this.getBronSchooljaren(jaar1, indicator, peiljaar, gecorrigeerdAantalJaren);

				return {
					indicator: indicator,
					key: cell.k,
					or_m_nr_verschil: jaar1.a.or_m_nr_verschil,
					or_m_nr_verschil_vorig: jaar2 ? jaar2.a.or_m_nr_verschil : undefined,
					actueelRow: {
						bron_schooljaren,
						or_m_nr_norm_gecorrigeerd: jaar1.a.or_m_nr_norm_gecorrigeerd,
						or_m_nr_norm_landelijk: jaar1.a.or_m_nr_norm_landelijk,
						or_m_nr_score: jaar1.a.or_m_nr_score,
						or_m_nr_score_vorig: jaar2 ? jaar2.a.or_m_nr_score : undefined,
						or_m_nr_verschil_tov: jaar2 ? this.getVerschilTov(indicator, jaar1, jaar2) : undefined,
						or_m_nr_verschil2_tov: jaar2 && jaar3 ? this.getVerschilTov(indicator, jaar2, jaar3) : undefined,
						or_m_q_leerlingen: jaar1.a.or_m_q_leerlingen,
						schooljaar1,
						schooljaar2,
					},
				};
			});
	}

	private getVerschilTov(indicator: string, jaar1: Level<OnderwijsresultatenA, number[]>, jaar2: Level<OnderwijsresultatenA, number[]>) {
		const keer = indicator === 'exc' ? 1 : 100;
		return jaar1.a.or_m_nr_score * keer - jaar2.a.or_m_nr_score * keer;
	}

	private getBronSchooljaren(jaar1: Level<OnderwijsresultatenA, number[]>, indicator: string, peiljaar: number, aantalJaren: number) {
		const inspectieSchooljaren = new Set((jaar1.a.or_m_nm_inspectie_schooljaren || '').split(','));
		const cumlaudeSchooljaren = new Set((jaar1.a.or_m_nm_cl_schooljaren || '').split(','));
		return getOnderwijsresultatenSchooljarenRange(`${peiljaar - 2}/${peiljaar - 1}`, aantalJaren, indicator).map((schooljaar) => {
			let bron = 'Geen data';
			if (inspectieSchooljaren.has(schooljaar)) bron = 'Inspectiedata';
			else if (cumlaudeSchooljaren.has(schooljaar)) bron = 'Bronsysteem';

			return [schooljaar, bron];
		});
	}

	private getHistorieCellRows(
		row: Path<OnderwijsresultatenA, number[]>,
		indicator: string,
		peiljaar: number,
		peiljaren: number[]
	): OnderwijsresultatenInputRow[] {
		const indicatorRows = row[1].c.find((level) => level.k == indicator);
		if (isUndefined(indicatorRows)) return [];

		const schooljaar1 = peiljaar;
		const schooljaar2 = peiljaar - 1;

		return indicatorRows.c.map((cell) => {
			const jaar1 = cell.c.find((level) => Number(level.k) == schooljaar1);
			const jaar2 = cell.c.find((level) => Number(level.k) == schooljaar2);

			const chartData = peiljaren.map((peiljaar) => {
				const peiljaarCell = cell.c.find((level) => Number(level.k) == peiljaar);

				const dataPoints: PointInfo[] = [];
				if (!isUndefined(peiljaarCell)) {
					dataPoints.push({ lineClass: 'landelijke-norm', qty: peiljaarCell.a.or_m_nr_norm_landelijk });
					if (indicator !== 'op') dataPoints.push({ lineClass: 'gecorrigeerde-norm', qty: peiljaarCell.a.or_m_nr_norm_gecorrigeerd });
					dataPoints.push({ lineClass: 'score', qty: peiljaarCell.a.or_m_nr_score });
				}

				return { label: `${peiljaar}`, data: dataPoints, path: peiljaarCell?.r[0] };
			});

			const minMax: [number, number] = [indicatorRows.a.minScoreNorm, indicatorRows.a.maxScoreNorm];
			const formatter = OnderwijsresultatenComponent.getFormatter(indicator);

			const linechartProps: LinechartProps<OnderwijsresultatenA> = {
				yAxis: createYAxis(minMax, 5, formatter),
				data: [{ label: '', path: cell.r[0], data: chartData }],
				lineNames: { 'landelijke-norm': 'Landelijke norm', 'gecorrigeerde-norm': 'Gecorrigeerde norm', score: 'Score' },
				formatter,
				onClick: (path) => this.onClick(path, indicator),
			};

			return {
				indicator: indicator,
				key: cell.k,
				or_m_nr_verschil: jaar1 ? jaar1.a.or_m_nr_verschil : undefined,
				or_m_nr_verschil_vorig: jaar1 && jaar2 ? jaar2.a.or_m_nr_verschil : undefined,
				linechartProps,
			};
		});
	}

	private static getFormatter(indicator: string): (x: number) => string {
		if (indicator === 'exc') return (x) => formatNumber(x, 'nl_NL', '1.2-2');

		return (x) => formatPercent(x, 'nl_NL', '1.1-1');
	}

	override getExportTable(
		context: DashboardContext<OnderwijsresultatenI, OnderwijsresultatenA, CardListConfig<OnderwijsresultatenI, Attributes, OnderwijsresultatenA>>,
		exportOptions: ExportOptions,
		title: string,
		filters: ExportFilter[]
	) {
		const INDICATOREN: { [code: string]: string } = {
			op: 'Onderwijspositie',
			obs: 'Onderbouwsnelheid',
			bbs: 'Bovenbouwsucces',
			exc: 'Examencijfers',
		};
		// Het path dat getValue meekrijgt, bevat [root, vestiging, indicator, niveau, peiljaar]
		const columns = concat<ExportColumnDef<OnderwijsresultatenA> & { getValue: (path: Path<OnderwijsresultatenA, number[]>) => any }>(
			[
				{ name: 'Vestiging', type: 'string', getValue: (path) => path[1].k },
				{ name: 'Niveau', type: 'string', getValue: (path) => path[3].k },
			],
			this.variant() === DashboardVariant.HISTORIE
				? [
						{
							name: 'Schooljaar',
							type: 'string',
							getValue: (path) => {
								const jaar = Number(last(path)!.a.or_fun_nr_peiljaar);
								return `${jaar - 2}/${jaar - 1}`;
							},
						},
					]
				: [],
			[
				{ name: 'Indicator', type: 'string', getValue: (path) => INDICATOREN[<string>path[2].k] },
				{
					name: 'Score',
					type: 'number',
					format: '1.2-2',
					getValue: (path) => last(path)!.a.or_m_nr_score * (path[2].k === 'exc' ? 1 : 100),
				},
				{
					name: 'Norm na correctie',
					type: 'number',
					format: '1.2-2',
					getValue: (path) => last(path)!.a.or_m_nr_norm_gecorrigeerd * (path[2].k === 'exc' ? 1 : 100),
				},
				{
					name: 'Kwalificatie',
					type: 'string',
					getValue: (path) => {
						const { or_m_nr_norm_gecorrigeerd, or_m_nr_score } = last(path)!.a;
						// De inspectie vergelijkt afgeronde waarden afgerond op 2 decimalen. 0,00% is "boven de norm"
						// 2 decimalen van een percentage is 4 decimalen van de breuk
						const decimalen = path[2].k === 'exc' ? 2 : 4;
						const or_m_nr_norm_gecorrigeerd_afgerond = roundToDecimals(or_m_nr_norm_gecorrigeerd, decimalen);
						const or_m_nr_score_afgerond = roundToDecimals(or_m_nr_score, decimalen);
						if (or_m_nr_score_afgerond >= or_m_nr_norm_gecorrigeerd_afgerond) return 'Boven de norm';
						return 'Onder de norm';
					},
				},
			]
		);
		const rows = flatMap(
			context.dataRoot!.r.map((path) =>
				last(path)!
					.xr!.filter((path) => this.variant() === DashboardVariant.HISTORIE || last(path)!.a.or_fun_nr_peiljaar == this.peiljaar)
					.map((path) => columns.map((col) => col.getValue(path)))
			)
		);

		return this.buildSinglePageExportTable<OnderwijsresultatenA>(exportOptions, title, filters, [{ columns, rows }]);
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getOnderwijsresultatenExportData(options);
	}

	override getDisplayOptions(): ExportFilter[] {
		return [
			{ label: 'Indicator over', value: indicatorOverOpties[this.aantalJaren] },
			{ label: 'Brondata', value: this.bron },
		];
	}

	private getIndicatorNaam(indicator: string): string {
		switch (indicator) {
			case 'op':
				return 'Onderwijspositie';
			case 'obs':
				return 'Onderbouwsnelheid';
			case 'bbs':
				return 'Bovenbouwsucces';
			case 'exc':
				return 'Examencijfers';
			default:
				throw new Error(`Niet herkende indicator ${indicator}`);
		}
	}

	protected readonly DashboardVariant = DashboardVariant;
}
